import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { SupaSQBuilding, SupaSQUnit } from '../types/supa';
import { LineItemProvisioningState } from '../types/order';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STEPS = ['Find address & unit', 'Confirm order', 'Result'];

type ProvisionContext = {
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  account_number: string | null;
  service_address: string;
  supa_tariff_id: string | null;
  product_name: string | null;
};

type Props = {
  open: boolean;
  orderId: string;
  cartLineIndex: number;
  /** Raw notes JSON from an already-complete supa_provision step — shows a status view instead of the search wizard. */
  existingNotes?: string | null;
  onClose: () => void;
  onSuccess: (updated: LineItemProvisioningState) => void;
};

type ExistingMeta = {
  supa_order_id?: string;
  service_id?: string;
  order_status?: string;
  buid?: string;
  manual?: boolean;
};

function buildingLabel(b: SupaSQBuilding): string {
  const addr = `${b.address_street_number} ${b.address_route}, ${b.address_locality} ${b.address_administrative_area_level_1} ${b.address_postal_code}`;
  return b.buildingName ? `${b.buildingName} — ${addr}` : addr;
}

export default function SupaNetworksProvisionModal({
  open,
  orderId,
  cartLineIndex,
  existingNotes,
  onClose,
  onSuccess,
}: Props) {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<'status' | 'wizard'>('wizard');
  const [existingMeta, setExistingMeta] = useState<ExistingMeta | null>(null);
  const [fulfilling, setFulfilling] = useState(false);
  const [fulfillError, setFulfillError] = useState<string | null>(null);
  const [fulfillSuccess, setFulfillSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [ctx, setCtx] = useState<ProvisionContext | null>(null);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [ctxError, setCtxError] = useState<string | null>(null);

  // Manual completion (used when the Supa API is unreachable and the order
  // was already placed directly through Supa's own portal)
  const [manualMode, setManualMode] = useState(false);
  const [manualSupaOrderId, setManualSupaOrderId] = useState('');
  const [manualServiceId, setManualServiceId] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);

  // Step 0 — building search
  const [buildings, setBuildings] = useState<SupaSQBuilding[]>([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<SupaSQBuilding | null>(null);
  const [searching, setSearching] = useState(false);
  const [sqError, setSqError] = useState<string | null>(null);
  const [units, setUnits] = useState<SupaSQUnit[] | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<SupaSQUnit | null>(null);

  // Step 1
  const [activationDate, setActivationDate] = useState<Date | null>(new Date());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Step 2
  const [resultMeta, setResultMeta] = useState<{ supa_order_id: string; service_id: string; buid: string } | null>(null);
  const [wasManual, setWasManual] = useState(false);

  const startWizard = () => {
    setViewMode('wizard');
    setLoadingCtx(true);
    api.adminSupaProvisionContext(orderId, cartLineIndex)
      .then((data) => setCtx(data))
      .catch((err: any) => {
        setCtxError(err.response?.data?.detail || 'Failed to load order context');
      })
      .finally(() => setLoadingCtx(false));

    setLoadingBuildings(true);
    api.publicSupaServicedBuildings()
      .then((data) => setBuildings(data.servicedBuildings || []))
      .catch(() => setBuildings([]))
      .finally(() => setLoadingBuildings(false));
  };

  const handleFulfill = async () => {
    setFulfilling(true);
    setFulfillError(null);
    try {
      const updated = await api.adminSupaFulfillOrder({ order_id: orderId, cart_line_index: cartLineIndex });
      const supaStep = updated.steps.find((s) => s.key === 'supa_provision');
      if (supaStep?.notes) {
        try { setExistingMeta(JSON.parse(supaStep.notes)); } catch { /* ignore */ }
      }
      setFulfillSuccess(true);
      onSuccess(updated);
    } catch (err: any) {
      setFulfillError(err.response?.data?.detail || err.message || 'Failed to fulfill order');
    } finally {
      setFulfilling(false);
    }
  };

  // Load context and buildings on open
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setCtx(null);
    setCtxError(null);
    setUnits(null);
    setSelectedUnit(null);
    setSelectedBuilding(null);
    setSqError(null);
    setSubmitError(null);
    setResultMeta(null);
    setActivationDate(new Date());
    setManualMode(false);
    setManualSupaOrderId('');
    setManualServiceId('');
    setManualReason('');
    setManualError(null);
    setWasManual(false);
    setFulfillError(null);
    setFulfillSuccess(false);

    let mode: 'status' | 'wizard' = 'wizard';
    let meta: ExistingMeta | null = null;
    if (existingNotes) {
      try {
        const parsed = JSON.parse(existingNotes);
        if (parsed && parsed.supa_order_id) {
          mode = 'status';
          meta = parsed;
        }
      } catch { /* malformed notes — fall back to the wizard */ }
    }
    setViewMode(mode);
    setExistingMeta(meta);

    if (mode !== 'wizard') return;

    setLoadingCtx(true);
    api.adminSupaProvisionContext(orderId, cartLineIndex)
      .then((data) => setCtx(data))
      .catch((err: any) => {
        setCtxError(err.response?.data?.detail || 'Failed to load order context');
      })
      .finally(() => setLoadingCtx(false));

    setLoadingBuildings(true);
    api.publicSupaServicedBuildings()
      .then((data) => setBuildings(data.servicedBuildings || []))
      .catch(() => setBuildings([]))
      .finally(() => setLoadingBuildings(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuildingSelect = async (building: SupaSQBuilding | null) => {
    setSelectedBuilding(building);
    setUnits(null);
    setSelectedUnit(null);
    setSqError(null);
    if (!building) return;

    setSearching(true);
    try {
      const sq = await api.adminSupaServiceQualification({
        StreetName: building.address_route,
        Suburb: building.address_locality,
        ...(building.address_street_number ? { StreetNo: building.address_street_number } : {}),
        ...(building.address_administrative_area_level_1 ? { State: building.address_administrative_area_level_1 } : {}),
        ...(building.address_postal_code ? { Postcode: building.address_postal_code } : {}),
        // networkNodeId is Supa's internal building ID — passing it as PlaceId uniquely
        // identifies the building when multiple buildings share the same street address
        ...(building.networkNodeId ? { PlaceId: building.networkNodeId } : {}),
      });
      if (sq.statusCode === '402') {
        setSqError('Address not found in Supa Networks coverage area.');
        return;
      }
      if (!sq.buildingUnits || sq.buildingUnits.length === 0) {
        setSqError('No units found for this building.');
        return;
      }
      setUnits(sq.buildingUnits);
    } catch (err: any) {
      setSqError(err.response?.data?.detail || 'Service qualification failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUnit || !ctx) return;
    setSubmitError(null);
    setSubmitting(true);
    const dateStr = activationDate ? format(activationDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
    try {
      const updated = await api.adminSupaNewOrder({
        order_id: orderId,
        cart_line_index: cartLineIndex,
        service_location: selectedUnit.rid,
        tariff_id: ctx.supa_tariff_id || '',
        activation_date: dateStr,
        customer_first_name: ctx.first_name,
        customer_last_name: ctx.last_name,
        customer_email: ctx.email,
        customer_phone: ctx.mobile_number,
        buid: selectedUnit.remoteId,
      });
      const supaStep = updated.steps.find((s) => s.key === 'supa_provision');
      if (supaStep?.notes) {
        try {
          setResultMeta(JSON.parse(supaStep.notes));
        } catch {
          setResultMeta({ supa_order_id: '', service_id: '', buid: selectedUnit.remoteId || '' });
        }
      }
      setActiveStep(2);
      onSuccess(updated);
    } catch (err: any) {
      setSubmitError(err.response?.data?.detail || err.message || 'Failed to place Supa Networks order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualComplete = async () => {
    if (!selectedUnit) return;
    setManualError(null);
    setManualSubmitting(true);
    try {
      const updated = await api.adminSupaMarkManualComplete({
        order_id: orderId,
        cart_line_index: cartLineIndex,
        buid: selectedUnit.remoteId || '',
        supa_order_id: manualSupaOrderId || undefined,
        service_id: manualServiceId || undefined,
        reason: manualReason || undefined,
      });
      const supaStep = updated.steps.find((s) => s.key === 'supa_provision');
      if (supaStep?.notes) {
        try {
          setResultMeta(JSON.parse(supaStep.notes));
        } catch {
          setResultMeta({ supa_order_id: manualSupaOrderId, service_id: manualServiceId, buid: selectedUnit.remoteId || '' });
        }
      }
      setWasManual(true);
      setActiveStep(2);
      onSuccess(updated);
    } catch (err: any) {
      setManualError(err.response?.data?.detail || err.message || 'Failed to mark step complete');
    } finally {
      setManualSubmitting(false);
    }
  };

  const statusChipColor = (code: string) => {
    if (code === '300') return 'success' as const;
    if (code === '301') return 'warning' as const;
    return 'default' as const;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={700}>Provision on Supa Networks</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        {viewMode === 'wizard' && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>
        )}

        <Divider />

        <DialogContent sx={{ minHeight: 300 }}>
          {viewMode === 'status' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {fulfillError && <Alert severity="error">{fulfillError}</Alert>}
              {fulfillSuccess && <Alert severity="success">Order fulfilled.</Alert>}

              <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2 }}>
                {existingMeta?.supa_order_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Supa Order ID</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                      {existingMeta.supa_order_id}
                    </Typography>
                  </Box>
                )}
                {existingMeta?.service_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Service ID</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                      {existingMeta.service_id}
                    </Typography>
                  </Box>
                )}
                {existingMeta?.buid && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">BUID</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                      {existingMeta.buid}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Order status</Typography>
                  <Chip
                    label={existingMeta?.order_status || 'Unknown'}
                    color={existingMeta?.order_status === 'Complete' ? 'success' : existingMeta?.order_status ? 'warning' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              {existingMeta?.manual && (
                <Alert severity="info">
                  This step was marked complete manually — no order was placed via the Supa API, so there's nothing to fulfill here.
                </Alert>
              )}
              {!existingMeta?.manual && existingMeta?.order_status && existingMeta.order_status !== 'Complete' && (
                <Alert severity="warning">
                  This order is not showing as Complete. If the customer's service is already active (check the Supa portal), use
                  Fulfill Order below to close out the order record.
                </Alert>
              )}
            </Box>
          ) : loadingCtx ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress />
            </Box>
          ) : ctxError ? (
            <Alert severity="error">{ctxError}</Alert>
          ) : (
            <>
              {/* ── Step 0: Building search & unit selection ── */}
              {activeStep === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Search for a building to find available units on the Supa Networks network.
                  </Typography>

                  <Autocomplete
                    options={buildings}
                    loading={loadingBuildings}
                    getOptionLabel={buildingLabel}
                    filterOptions={(options, { inputValue }) => {
                      const q = inputValue.toLowerCase();
                      if (!q) return options.slice(0, 50);
                      return options.filter((b) =>
                        b.buildingName?.toLowerCase().includes(q) ||
                        b.address_route?.toLowerCase().includes(q) ||
                        b.address_locality?.toLowerCase().includes(q) ||
                        `${b.address_street_number} ${b.address_route}`.toLowerCase().includes(q)
                      ).slice(0, 50);
                    }}
                    value={selectedBuilding}
                    onChange={(_, b) => handleBuildingSelect(b)}
                    isOptionEqualToValue={(a, b) => a.place_id === b.place_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search building"
                        placeholder="Building name, street or suburb…"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingBuildings && <CircularProgress size={16} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, b) => (
                      <li {...props} key={b.place_id || b.networkNodeId}>
                        <Box py={0.25}>
                          {b.buildingName && (
                            <Typography variant="body2" fontWeight={600}>{b.buildingName}</Typography>
                          )}
                          <Typography variant="body2" color={b.buildingName ? 'text.secondary' : 'text.primary'}>
                            {b.address_street_number} {b.address_route}, {b.address_locality} {b.address_administrative_area_level_1} {b.address_postal_code}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />

                  {searching && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Checking service availability…</Typography>
                    </Box>
                  )}

                  {sqError && <Alert severity="warning">{sqError}</Alert>}

                  {units && units.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Select a unit ({units.length} found)
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Unit</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>BUID (remoteId)</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {units.map((u) => (
                            <TableRow
                              key={u.rid}
                              hover
                              selected={selectedUnit?.rid === u.rid}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setSelectedUnit(u)}
                            >
                              <TableCell>{u.unitNo || '—'}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{u.addressLine1}</Typography>
                                {u.addressLine2 && (
                                  <Typography variant="caption" color="text.secondary">{u.addressLine2}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={u.statusDescription}>
                                  <Chip
                                    label={u.statusCode === '300' ? 'Available' : u.statusCode === '301' ? 'Provisioned' : u.statusCode === '302' ? 'Churn' : u.statusCode}
                                    color={statusChipColor(u.statusCode)}
                                    size="small"
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {u.remoteId || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {selectedUnit?.rid === u.rid && (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Step 1: Confirm ── */}
              {activeStep === 1 && ctx && selectedUnit && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  {submitError && <Alert severity="error">{submitError}</Alert>}

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {ctx.first_name} {ctx.last_name}
                      </Typography>
                      <Typography variant="body2">{ctx.email}</Typography>
                      <Typography variant="body2">{ctx.mobile_number}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account number</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {ctx.account_number || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Selected unit</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedUnit.unitNo ? `Unit ${selectedUnit.unitNo} — ` : ''}{selectedUnit.addressLine1}
                      </Typography>
                      {selectedUnit.addressLine2 && (
                        <Typography variant="body2" color="text.secondary">{selectedUnit.addressLine2}</Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        RID: <span style={{ fontFamily: 'monospace' }}>{selectedUnit.rid}</span>
                      </Typography>
                      {selectedUnit.remoteId && (
                        <Typography variant="body2" color="text.secondary">
                          BUID: <span style={{ fontFamily: 'monospace' }}>{selectedUnit.remoteId}</span>
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Plan / Tariff ID</Typography>
                      {ctx.product_name && (
                        <Typography variant="body2" fontWeight={600}>{ctx.product_name}</Typography>
                      )}
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                        {ctx.supa_tariff_id || <span style={{ color: 'red' }}>Tariff ID not set on product</span>}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ maxWidth: 220 }}>
                    <DatePicker
                      label="Activation date"
                      value={activationDate}
                      onChange={(d) => setActivationDate(d)}
                      minDate={new Date()}
                      format="dd/MM/yyyy"
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Box>

                  <Alert severity="info" sx={{ mt: 1 }}>
                    Placing this order will activate the service on Supa Networks.
                  </Alert>

                  {user?.is_admin && (
                    <Box sx={{ mt: 1 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      {!manualMode ? (
                        <Button size="small" color="warning" onClick={() => setManualMode(true)}>
                          Supa API not working? Mark as manually provisioned instead
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Alert severity="warning">
                            Only use this if the order has already been placed directly through Supa
                            Networks' own portal. This records the step as complete without calling
                            the Supa API — nothing will actually be provisioned by this action.
                          </Alert>
                          {manualError && <Alert severity="error">{manualError}</Alert>}
                          <TextField
                            label="Supa Order ID (optional)"
                            size="small"
                            value={manualSupaOrderId}
                            onChange={(e) => setManualSupaOrderId(e.target.value)}
                          />
                          <TextField
                            label="Supa Service ID (optional)"
                            size="small"
                            value={manualServiceId}
                            onChange={(e) => setManualServiceId(e.target.value)}
                          />
                          <TextField
                            label="Reason"
                            size="small"
                            placeholder="e.g. Supa API unreachable, placed manually via their portal"
                            value={manualReason}
                            onChange={(e) => setManualReason(e.target.value)}
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => setManualMode(false)}
                              disabled={manualSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              onClick={handleManualComplete}
                              disabled={manualSubmitting}
                              startIcon={manualSubmitting ? <CircularProgress size={16} /> : null}
                            >
                              {manualSubmitting ? 'Marking complete…' : 'Mark step complete'}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Step 2: Result ── */}
              {activeStep === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 3, pb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 56, color: wasManual ? 'warning.main' : 'success.main' }} />
                  <Typography variant="h6" fontWeight={700}>
                    {wasManual ? 'Step marked complete (manual)' : 'Order placed successfully'}
                  </Typography>
                  {wasManual && (
                    <Alert severity="warning" sx={{ width: '100%', maxWidth: 420 }}>
                      No order was placed via the Supa API — this only updates our records to reflect
                      the order you placed manually on Supa's portal.
                    </Alert>
                  )}
                  {resultMeta && (
                    <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, width: '100%', maxWidth: 420 }}>
                      {resultMeta.supa_order_id && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Supa Order ID</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {resultMeta.supa_order_id}
                          </Typography>
                        </Box>
                      )}
                      {resultMeta.service_id && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Service ID</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {resultMeta.service_id}
                          </Typography>
                        </Box>
                      )}
                      {resultMeta.buid && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">BUID (IPoE Remote ID)</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {resultMeta.buid}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    The BUID has been saved to internal notes and will pre-fill the RADIUS credentials step.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {viewMode === 'status' && (
            <>
              <Button onClick={onClose}>Close</Button>
              <Button onClick={startWizard}>Re-run provisioning</Button>
              {!existingMeta?.manual && existingMeta?.supa_order_id && existingMeta?.order_status !== 'Complete' && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleFulfill}
                  disabled={fulfilling}
                  startIcon={fulfilling ? <CircularProgress size={16} /> : null}
                >
                  {fulfilling ? 'Fulfilling…' : 'Fulfill Order'}
                </Button>
              )}
            </>
          )}
          {viewMode === 'wizard' && activeStep === 0 && (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(1)}
                disabled={!selectedUnit || loadingCtx}
              >
                Next
              </Button>
            </>
          )}
          {viewMode === 'wizard' && activeStep === 1 && (
            <>
              <Button onClick={() => { setSubmitError(null); setActiveStep(0); }} disabled={submitting}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !ctx?.supa_tariff_id}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                {submitting ? 'Placing order…' : 'Place order'}
              </Button>
            </>
          )}
          {viewMode === 'wizard' && activeStep === 2 && (
            <Button variant="contained" onClick={onClose}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
